// src/pages/Orders/OrdersOnMap.jsx
import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@services/api";
import { useOrderDetailsFilterQuery, useOrderStatusesQuery } from "@queries/order";
import { useTimeSlotsQuery } from "@queries/time_slots";
import { useMarketplace } from "@context/MarketplaceContext";
import { loadGoogleMaps } from "@lib/googleMapsLoader";
import { paymentInfo } from "@lib/paymentMethods";
import "@styles/opencart-admin.css";
import "../Logistics/driver-assign.css";

const DRIVER_PALETTE = ["#2563eb", "#16a34a", "#db2777", "#7c3aed", "#0891b2", "#ca8a04", "#dc2626", "#4d7c0f", "#9333ea", "#0d9488"];

const TYPES = [
  "express",
  "housekeeping",
  "carpets",
  "car_cleaning",
  "normal",
  "skip",
  "complementary",
  "disinfection",
  "furniture",
  "chemicals",
];

const AMMAN = { lat: 31.9539, lng: 35.9106 };

// Amman zone overlays drawn under the order markers on the map.
// Directional tiling of the Greater Amman urban area, anchored on real OSM
// coordinates (Abdoun=West, Downtown=Central, Marka=East, Sweileh=North, and
// the southern extent toward Yadudah / Airport road). Zones share edges so they
// tile without gaps/overlap.
const AMMAN_ZONES = {
  // Northern strip: Sweileh, Jubeiha, Abu Nseir, Shafa Badran, Tla' Al Ali (north).
  North_Amman: {
    color: "#4A90E2",
    coordinates: [
      { lat: 32.09, lng: 35.79 },
      { lat: 32.09, lng: 36.05 },
      { lat: 31.99, lng: 36.05 },
      { lat: 31.99, lng: 35.79 },
    ],
  },
  // Western districts: Abdoun, Sweifieh, Um Uthaina, Khalda, Deir Ghbar, Dabouq.
  West_Amman: {
    color: "#50E3C2",
    coordinates: [
      { lat: 31.99, lng: 35.79 },
      { lat: 31.99, lng: 35.9 },
      { lat: 31.92, lng: 35.9 },
      { lat: 31.92, lng: 35.79 },
    ],
  },
  // Central core: Downtown (Wasat al-Balad), Jabal Amman, Al Weibdeh, Ras al-Ain.
  Central_Amman: {
    color: "#F5A623",
    coordinates: [
      { lat: 31.99, lng: 35.9 },
      { lat: 31.99, lng: 35.96 },
      { lat: 31.92, lng: 35.96 },
      { lat: 31.92, lng: 35.9 },
    ],
  },
  // Eastern districts: Marka, Al Nuzha, Basman, Al Yarmouk, Al Qwaismeh (east).
  East_Amman: {
    color: "#D0021B",
    coordinates: [
      { lat: 31.99, lng: 35.96 },
      { lat: 31.99, lng: 36.05 },
      { lat: 31.92, lng: 36.05 },
      { lat: 31.92, lng: 35.96 },
    ],
  },
  // Southern area: Al Qwaismeh (south), Umm Quseir, Al Yadudah, Airport road.
  South_Amman: {
    color: "#7ED321",
    coordinates: [
      { lat: 31.92, lng: 35.83 },
      { lat: 31.92, lng: 36.03 },
      { lat: 31.8, lng: 36.03 },
      { lat: 31.8, lng: 35.83 },
    ],
  },
};

function zoneCentroid(coords) {
  const n = coords.length || 1;
  return {
    lat: coords.reduce((s, c) => s + c.lat, 0) / n,
    lng: coords.reduce((s, c) => s + c.lng, 0) / n,
  };
}

const getStatusId = (r) => Number(r?.order_status_id ?? r?.order?.order_status_id);

const isJordanLat = (v) => v >= 29 && v <= 33.8;
const isJordanLng = (v) => v >= 34.5 && v <= 39.5;
const isInJordan = (p) => isJordanLat(p.lat) && isJordanLng(p.lng);

const parseCsv = (v) =>
  String(v || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

// Ray-casting point-in-polygon (no Google geometry lib needed).
function pointInPolygon(pt, coords) {
  let inside = false;
  for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
    const xi = coords[i].lng, yi = coords[i].lat;
    const xj = coords[j].lng, yj = coords[j].lat;
    const hit = ((yi > pt.lat) !== (yj > pt.lat)) &&
      pt.lng < ((xj - xi) * (pt.lat - yi)) / (yj - yi) + xi;
    if (hit) inside = !inside;
  }
  return inside;
}

// Name of the zone that contains the point, or null.
function zoneForPoint(pt, zones) {
  if (!pt || !Number.isFinite(pt.lat) || !Number.isFinite(pt.lng)) return null;
  for (const [name, z] of Object.entries(zones || {})) {
    const coords = (z.coordinates || [])
      .filter((c) => Number.isFinite(Number(c.lat)) && Number.isFinite(Number(c.lng)))
      .map((c) => ({ lat: Number(c.lat), lng: Number(c.lng) }));
    if (coords.length >= 3 && pointInPolygon(pt, coords)) return name;
  }
  return null;
}

const fromSearch = (sp) => {
  const orderTypeParam = sp.get("order_type");
  return {
    day: sp.get("day") || new Date().toISOString().slice(0, 10),
    time_slot: sp.get("time_slot") || "",
    order_status_id: sp.get("order_status_id") || "",
    order_type: orderTypeParam !== null ? parseCsv(orderTypeParam) : ["normal", "skip", "express", "carpets", "chemicals"],
    area: parseCsv(sp.get("area")), // empty = all areas
    page: Math.max(1, parseInt(sp.get("page") || "1", 10)),
    page_size: Math.max(1, parseInt(sp.get("page_size") || "100", 10)),
  };
};

const toSearch = (f) => {
  const sp = new URLSearchParams();
  if (f.day) sp.set("day", f.day);
  if (f.time_slot) sp.set("time_slot", f.time_slot);
  if (f.order_status_id) sp.set("order_status_id", f.order_status_id);
  if (Array.isArray(f.order_type) && f.order_type.length) sp.set("order_type", f.order_type.join(","));
  if (Array.isArray(f.area) && f.area.length) sp.set("area", f.area.join(","));

  sp.set("page", String(f.page || 1));
  sp.set("page_size", String(f.page_size || 100));
  return sp;
};

const STATUS_COLOR = {
  2: "#1e90ff",
  5: "#8e44ad",
  17: "#2ecc71",
  18: "#e67e22",
};

// A teardrop map PIN in the given colour. Used for normal markers AND in assign
// mode (coloured by driver) + the marquee highlight — so markers always stay pins.
function pinIcon(color, scale = 2.5) {
  return {
    path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z",
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
    scale,
    anchor: new window.google.maps.Point(12, 24),
  };
}

function markerIconForOrder(row) {
  const id = Number(row?.order_status_id ?? row?.order?.order_status_id);
  const color = STATUS_COLOR[id] || "#e74c3c";
  return pinIcon(color);
}

// Inline driver→area coverage editor (used inside the assign panel on the map page).
function AssignCoverage({ zones, drivers, onChanged }) {
  const [rows, setRows] = React.useState([]);
  const [savingId, setSavingId] = React.useState(null);
  React.useEffect(() => {
    setRows((drivers || []).map((d) => ({ ...d, zoneSet: new Set((d.areas || []).map((a) => a.zone_key)) })));
  }, [drivers]);
  const toggle = (i, zk) => setRows((rs) => rs.map((d, idx) => {
    if (idx !== i) return d;
    const s = new Set(d.zoneSet); s.has(zk) ? s.delete(zk) : s.add(zk); return { ...d, zoneSet: s };
  }));
  const save = async (i) => {
    const d = rows[i]; setSavingId(d.driver_id);
    try { await api.put(`/assignments/drivers/${d.driver_id}/areas`, { areas: [...d.zoneSet].map((zk) => ({ zone_key: zk })) }); onChanged?.(); }
    finally { setSavingId(null); }
  };
  return (
    <div className="sa-coverage">
      {rows.map((d, i) => (
        <div className="sa-cov-row" key={d.driver_id}>
          <strong>{d.name}</strong>
          <div className="sa-cov-zones">
            {Object.entries(zones).map(([name, z]) => (
              <label key={name} className={"sa-cov-zone" + (d.zoneSet.has(name) ? " on" : "")}>
                <input type="checkbox" checked={d.zoneSet.has(name)} onChange={() => toggle(i, name)} />
                <span className="da-dot" style={{ background: z.color }} />
                {z.name_ar || name.replace(/_/g, " ")}
              </label>
            ))}
          </div>
          <button className="oc-btn" onClick={() => save(i)} disabled={savingId === d.driver_id}>{savingId === d.driver_id ? "…" : "Save"}</button>
        </div>
      ))}
      {!rows.length && <div className="da-muted">No active drivers.</div>}
    </div>
  );
}

export default function OrdersOnMap() {
  const [search, setSearch] = useSearchParams();
  const [filters, setFilters] = React.useState(fromSearch(search));
  React.useEffect(() => setFilters(fromSearch(search)), [search]);

  const { data: timeSlots = [], isLoading: tsLoading } = useTimeSlotsQuery(filters.day);

  const queryFilters = React.useMemo(
    () => ({
      from_date: filters.day || undefined,
      to_date: filters.day || undefined,
      time_slot: filters.time_slot || undefined,
      order_status_id: filters.order_status_id || undefined,
      order_type: filters.order_type?.length ? filters.order_type : undefined,
      page: filters.page || 1,
      page_size: filters.page_size || 100,
    }),
    [filters]
  );

  const { data: resp, isLoading, isFetching } = useOrderDetailsFilterQuery(queryFilters);
  const apiRows = resp?.data ?? [];

  // Editable Amman zones (from the Map Zones settings page); falls back to the
  // baked-in defaults until the API responds.
  const { data: zonesData } = useQuery({
    queryKey: ["map-zones"],
    queryFn: () => api.get("/map-zones").then((r) => r.data?.data || {}),
    staleTime: 5 * 60 * 1000,
  });
  const zones = zonesData && Object.keys(zonesData).length ? zonesData : AMMAN_ZONES;

  const rows = React.useMemo(() => {
    let out = apiRows;

    // order type multi-filter
    if (filters.order_type?.length) {
      const set = new Set(filters.order_type);
      out = out.filter((r) => set.has(String(r.type || "").trim()));
    }

    // Tag each order with the area (zone) its coordinate falls in.
    out = out.map((r) => ({ ...r, _area: zoneForPoint(getOrderCoords(r), zones) }));

    // area (zone) multi-filter ("—" = outside all defined areas)
    if (filters.area?.length) {
      const set = new Set(filters.area);
      out = out.filter((r) => set.has(r._area ?? "—"));
    }

    return out;
  }, [apiRows, filters.order_type, filters.area, zones]);

  const meta = resp?.meta ?? { page: filters.page, page_size: filters.page_size, total: rows.length, pages: 1 };

  const { data: statuses = [] } = useOrderStatusesQuery();

  const apply = () => setSearch(toSearch({ ...filters, page: 1 }), { replace: true });
  const setPage = (p) => setSearch(toSearch({ ...filters, page: p }), { replace: true });

  const [gmReady, setGmReady] = React.useState(false);
  React.useEffect(() => {
    let ok = true;
    loadGoogleMaps().then(() => ok && setGmReady(true));
    return () => {
      ok = false;
    };
  }, []);

  const toggleOrderType = (type, checked) => {
    setFilters((f) => {
      const current = f.order_type || [];
      if (checked) {
        return { ...f, order_type: [...current, type] };
      } else {
        return { ...f, order_type: current.filter((t) => t !== type) };
      }
    });
  };

  const clearOrderType = () => setFilters((f) => ({ ...f, order_type: [] }));

  const toggleArea = (name, checked) => {
    setFilters((f) => {
      const current = f.area || [];
      return { ...f, area: checked ? [...current, name] : current.filter((a) => a !== name) };
    });
  };
  const clearArea = () => setFilters((f) => ({ ...f, area: [] }));

  // Order count per area (over the type-filtered set, ignoring the area filter).
  const areaCounts = React.useMemo(() => {
    const c = {};
    for (const r of apiRows) {
      if (filters.order_type?.length && !new Set(filters.order_type).has(String(r.type || "").trim())) continue;
      const a = zoneForPoint(getOrderCoords(r), zones) || "—";
      c[a] = (c[a] || 0) + 1;
    }
    return c;
  }, [apiRows, filters.order_type, zones]);

  const failedRows = React.useMemo(() => rows.filter((r) => !getOrderCoords(r)), [rows]);

  /* ---------------- Assign-to-drivers mode (hidden until the button) ---------------- */
  const { marketplaces, isMarketplaceAdmin } = useMarketplace();
  const franchiseOptions = (marketplaces && marketplaces.length)
    ? marketplaces.map((m) => ({ id: Number(m.franchise_id), name: m.name }))
    : [{ id: 0, name: "WashyWash" }, { id: 4, name: "FlashWash" }];

  const [assignOpen, setAssignOpen] = React.useState(false);
  const [assignFranchise, setAssignFranchise] = React.useState(franchiseOptions[0].id);
  const [assignType, setAssignType] = React.useState("dropoff");
  const [preferSameArea, setPreferSameArea] = React.useState(true);
  const [maxPerSlot, setMaxPerSlot] = React.useState("");
  const [showCoverage, setShowCoverage] = React.useState(false);
  const [visibleOnly, setVisibleOnly] = React.useState(true);
  const [bulkDriver, setBulkDriver] = React.useState("");
  const [driversRaw, setDriversRaw] = React.useState([]);
  const [proposal, setProposal] = React.useState(null);
  const [selectedDriver, setSelectedDriver] = React.useState(null);
  const [assignBusy, setAssignBusy] = React.useState(false);
  const [assignMsg, setAssignMsg] = React.useState(null);
  // Marquee (drag-to-select on the map) → bulk assign the selected orders.
  const [marqueeMode, setMarqueeMode] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [marqueeDriver, setMarqueeDriver] = React.useState("");
  const applyMarquee = () => {
    const drv = marqueeDriver ? Number(marqueeDriver) : null;
    selectedIds.forEach((id) => reassign(id, drv));
    setSelectedIds([]);
  };

  const loadDrivers = React.useCallback(() => {
    api.get("/assignments/drivers").then((r) => setDriversRaw(r.data?.data || [])).catch(() => {});
  }, []);
  React.useEffect(() => { if (assignOpen) loadDrivers(); }, [assignOpen, loadDrivers]);

  const runAssign = async () => {
    setAssignBusy(true); setAssignMsg(null); setProposal(null); setSelectedDriver(null);
    try {
      const { data } = await api.post("/assignments/preview", {
        date: filters.day, franchise_id: assignFranchise, type: assignType,
        prefer_same_area: preferSameArea, max_per_slot: maxPerSlot === "" ? undefined : Number(maxPerSlot),
        // Assign exactly the orders currently shown on the map (what you review = what gets assigned).
        order_ids: visibleOnly ? rows.map((r) => r.order_id).filter(Boolean) : undefined,
      });
      setProposal(data);
    } catch (e) {
      setAssignMsg({ type: "err", text: e?.response?.data?.message || "Failed to build the proposal." });
    } finally { setAssignBusy(false); }
  };
  // Build proposal.drivers from the loaded coverage.
  const proposalDrivers = React.useCallback(() =>
    driversRaw.map((d) => ({ driver_id: d.driver_id, name: d.name, zones: (d.areas || []).map((a) => a.zone_key) })), [driversRaw]);
  const driverName = React.useCallback((id) => driversRaw.find((d) => d.driver_id === id)?.name ?? null, [driversRaw]);

  // Turn a map row into a proposal order (client-side area from its coords).
  const rowToOrder = React.useCallback((r, driverId = null) => {
    const pos = getOrderCoords(r);
    const area = pos ? zoneForPoint(pos, zones) : null;
    return {
      order_id: r.order_id, name: r.customer || r.name || "",
      lat: pos?.lat ?? null, lng: pos?.lng ?? null, area,
      slot_id: null, slot_label: "", sort_order: 0,
      driver_id: driverId, driver_name: driverId ? driverName(driverId) : null,
      reason: driverId ? null : "unassigned",
    };
  }, [zones, driverName]);

  // Snapshot the currently-shown orders into the working proposal (all -> driverId).
  const buildFromRows = React.useCallback((driverId = null) => {
    const orders = rows.map((r) => rowToOrder(r, driverId));
    setProposal({ orders, drivers: proposalDrivers() });
  }, [rows, rowToOrder, proposalDrivers]);

  // Change one order's driver; add it from the map if it isn't in the proposal yet.
  const reassign = (orderId, driverId) => setProposal((p) => {
    const base = p || { orders: [], drivers: proposalDrivers() };
    if (base.orders.some((o) => o.order_id === orderId)) {
      return { ...base, orders: base.orders.map((o) => o.order_id !== orderId ? o : { ...o, driver_id: driverId, driver_name: driverId ? driverName(driverId) : null, reason: driverId ? null : "unassigned" }) };
    }
    const r = rows.find((rr) => rr.order_id === orderId);
    return { ...base, orders: r ? [...base.orders, rowToOrder(r, driverId)] : base.orders };
  });

  // Bulk: assign every shown order to one driver (or unassign with null).
  const assignShownTo = (driverId) => {
    const ids = new Set(rows.map((r) => r.order_id));
    setProposal((p) => {
      if (!p) return { orders: rows.map((r) => rowToOrder(r, driverId)), drivers: proposalDrivers() };
      const covered = new Set(p.orders.map((o) => o.order_id));
      const updated = p.orders.map((o) => ids.has(o.order_id) ? { ...o, driver_id: driverId, driver_name: driverId ? driverName(driverId) : null, reason: driverId ? null : "unassigned" } : o);
      const added = rows.filter((r) => ids.has(r.order_id) && !covered.has(r.order_id)).map((r) => rowToOrder(r, driverId));
      return { ...p, orders: [...updated, ...added] };
    });
  };

  const onDropCard = (e, driverId) => { e.preventDefault(); const id = Number(e.dataTransfer.getData("text/plain")); if (id) reassign(id, driverId); };

  // When assign mode opens, show the shown orders immediately (all unassigned) so
  // they can be reviewed, clicked and edited before assigning.
  React.useEffect(() => {
    if (assignOpen && driversRaw.length && rows.length && !proposal) buildFromRows(null);
  }, [assignOpen, driversRaw.length, rows.length, proposal, buildFromRows]);
  const commitAssign = async () => {
    const assignments = (proposal?.orders || []).filter((o) => o.driver_id)
      .map((o) => ({ order_id: o.order_id, driver_id: o.driver_id, slot_id: o.slot_id, sort_order: o.sort_order ?? 0 }));
    if (!assignments.length) { setAssignMsg({ type: "err", text: "Nothing to confirm." }); return; }
    setAssignBusy(true); setAssignMsg(null);
    try {
      const { data } = await api.post("/assignments/commit", { type: assignType, assignments });
      setAssignMsg({ type: "ok", text: `Confirmed — ${data.committed} orders assigned.` });
    } catch (e) {
      setAssignMsg({ type: "err", text: e?.response?.data?.message || "Failed to confirm." });
    } finally { setAssignBusy(false); }
  };

  const driverColors = React.useMemo(() => {
    const m = {}; (proposal?.drivers || []).forEach((d, i) => { m[d.driver_id] = DRIVER_PALETTE[i % DRIVER_PALETTE.length]; });
    return m;
  }, [proposal]);
  // order_id -> {color, driver_id, driver_name} for recolouring markers + the click menu.
  const assignment = React.useMemo(() => {
    if (!proposal) return null;
    const m = {};
    for (const o of proposal.orders) m[o.order_id] = { color: o.driver_id ? (driverColors[o.driver_id] || "#9ca3af") : "#9ca3af", driver_id: o.driver_id, driver_name: o.driver_name };
    return m;
  }, [proposal, driverColors]);

  const byDriver = {}; const unassigned = [];
  for (const o of proposal?.orders || []) { if (o.driver_id) (byDriver[o.driver_id] ||= []).push(o); else unassigned.push(o); }
  const liveSummary = proposal ? { total: proposal.orders.length, assigned: proposal.orders.length - unassigned.length, unassigned: unassigned.length } : null;
  const zColor = (a) => zones[a]?.color || "#9ca3af";

  return (
    <div className="oc-content">
      {(isLoading || isFetching) && <div className="oc-card">Loading…</div>}

      <div className="oc-header">
        <h1>Orders Map</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Orders by Date</span>
        </nav>
      </div>

      <div className="oc-card oc-filter">
        <div className="title">Filter</div>

        {/* Improved flex layout for clearer filters */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end" }}>
          <div>
            <label className="lbl">Delivery/Pickup Day</label>
            <input
              type="date"
              value={filters.day}
              onChange={(e) => setFilters((f) => ({ ...f, day: e.target.value }))}
              className="oc-input"
              style={{ width: "200px" }}
            />
          </div>

          <div>
            <label className="lbl">Time Slot</label>
            <select
              value={filters.time_slot}
              onChange={(e) => setFilters((f) => ({ ...f, time_slot: e.target.value }))}
              className="oc-input"
              disabled={tsLoading}
              style={{ width: "200px" }}
            >
              <option value="">All</option>
              {timeSlots.map((t) => {
                const value = t?.value ?? t;
                const label = t?.label ?? t;
                return (
                  <option key={value} value={value}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <label className="lbl">Status</label>
            <select
              value={filters.order_status_id}
              onChange={(e) => setFilters((f) => ({ ...f, order_status_id: e.target.value }))}
              className="oc-input"
              style={{ width: "200px" }}
            >
              <option value="">All</option>
              {statuses.map((s) => (
                <option key={s.order_status_id} value={s.order_status_id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <button className="oc-btn" onClick={apply}>
              Filter
            </button>
          </div>
        </div>

        <div style={{ marginTop: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <label className="lbl" style={{ marginBottom: 0 }}>Order Types</label>
            <button
              className="oc-btn ghost"
              style={{ padding: "4px 8px", fontSize: 12, height: "auto", minHeight: "auto" }}
              type="button"
              onClick={clearOrderType}
              disabled={!filters.order_type?.length}
            >
              Clear All
            </button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {TYPES.map((t) => {
              const isSelected = filters.order_type?.includes(t);
              return (
                <div
                  key={t}
                  onClick={() => toggleOrderType(t, !isSelected)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "20px",
                    border: isSelected ? "1px solid #1e90ff" : "1px solid #ddd",
                    backgroundColor: isSelected ? "#e6f2ff" : "#f9f9f9",
                    color: isSelected ? "#1e90ff" : "#555",
                    cursor: "pointer",
                    fontSize: "13px",
                    userSelect: "none",
                    transition: "all 0.2s"
                  }}
                >
                  {labelForType(t)}
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ marginTop: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <label className="lbl" style={{ marginBottom: 0 }}>Areas</label>
            <button
              className="oc-btn ghost"
              style={{ padding: "4px 8px", fontSize: 12, height: "auto", minHeight: "auto" }}
              type="button"
              onClick={clearArea}
              disabled={!filters.area?.length}
            >
              All Areas
            </button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {Object.entries(zones).map(([name, z]) => {
              const isSelected = filters.area?.includes(name);
              const label = z.name_ar || String(name).replace(/_/g, " ");
              return (
                <div
                  key={name}
                  onClick={() => toggleArea(name, !isSelected)}
                  style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "6px 12px", borderRadius: "20px",
                    border: isSelected ? `1px solid ${z.color}` : "1px solid #ddd",
                    backgroundColor: isSelected ? `${z.color}22` : "#f9f9f9",
                    color: isSelected ? "#333" : "#555",
                    cursor: "pointer", fontSize: "13px", userSelect: "none", transition: "all 0.2s",
                  }}
                >
                  <span style={{ width: 10, height: 10, borderRadius: 999, background: z.color }} />
                  {label}
                  <span style={{ opacity: 0.6 }}>({areaCounts[name] || 0})</span>
                </div>
              );
            })}
            {(areaCounts["—"] || 0) > 0 && (
              <div
                onClick={() => toggleArea("—", !filters.area?.includes("—"))}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: "20px",
                  border: filters.area?.includes("—") ? "1px solid #888" : "1px solid #ddd",
                  backgroundColor: filters.area?.includes("—") ? "#eee" : "#f9f9f9",
                  color: "#555", cursor: "pointer", fontSize: "13px", userSelect: "none",
                }}
                title="Orders outside all defined areas"
              >
                Outside areas <span style={{ opacity: 0.6 }}>({areaCounts["—"]})</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Single map only */}
      <div className="oc-card" style={{ marginTop: 16 }}>
        <div className="oc-toolbar" style={{ justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <strong>
              Orders Map • Date:{filters.day || "-"} • Orders count:{rows.length}
              {filters.order_type?.length ? ` • Types: ${filters.order_type.map(labelForType).join(", ")}` : ""}
            </strong>
            <button
              className="oc-btn primary"
              style={{ marginLeft: 12, padding: "4px 12px", fontSize: 13, height: "auto", minHeight: "auto" }}
              onClick={() => setAssignOpen((v) => !v)}
            >
              {assignOpen ? "Close assign" : "⚡ Assign to drivers"}
            </button>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {!proposal && Object.entries(STATUS_COLOR).map(([sid, color]) => (
              <span key={sid} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.9 }}>
                <span style={{ width: 10, height: 10, borderRadius: 999, background: color }} />
                {statuses.find(s => Number(s.order_status_id) === Number(sid))?.name || `Status ${sid}`}
              </span>
            ))}
            {!proposal && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, opacity: 0.9 }}>
                <span style={{ width: 10, height: 10, borderRadius: 999, background: "#e74c3c" }} />
                Other
              </span>
            )}
            {proposal && (proposal.drivers || []).map((d) => (
              <span key={d.driver_id} className={"sa-leg" + (selectedDriver === d.driver_id ? " on" : "")}
                    onClick={() => setSelectedDriver(selectedDriver === d.driver_id ? null : d.driver_id)}>
                <span className="sa-dot" style={{ background: driverColors[d.driver_id] }} /> {d.name} ({(byDriver[d.driver_id] || []).length})
              </span>
            ))}
            {proposal && <span className="sa-leg"><span className="sa-dot" style={{ background: "#9ca3af" }} /> Unassigned ({unassigned.length})</span>}
          </div>
        </div>

        {/* Assign panel — hidden until the "Assign to drivers" button is clicked. */}
        {assignOpen && (
          <div className="oc-card" style={{ margin: "0 0 10px", background: "#f8fafc" }}>
            <div className="sa-controls" style={{ padding: 4 }}>
              <label>Marketplace
                <select className="oc-input" value={assignFranchise} onChange={(e) => setAssignFranchise(Number(e.target.value))}>
                  {franchiseOptions.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </label>
              <label>Type
                <select className="oc-input" value={assignType} onChange={(e) => setAssignType(e.target.value)}>
                  <option value="dropoff">Delivery (drop-off)</option>
                  <option value="pickup">Pickup</option>
                </select>
              </label>
              <label>Max/slot <input className="oc-input" type="number" min="1" style={{ width: 80 }} value={maxPerSlot} onChange={(e) => setMaxPerSlot(e.target.value)} placeholder="none" /></label>
              <label className="sa-check"><input type="checkbox" checked={preferSameArea} onChange={(e) => setPreferSameArea(e.target.checked)} /> Cluster by area</label>
              <label className="sa-check"><input type="checkbox" checked={visibleOnly} onChange={(e) => setVisibleOnly(e.target.checked)} /> Only the {rows.length} orders shown</label>
              <button className="oc-btn primary" onClick={runAssign} disabled={assignBusy}>{assignBusy ? "…" : `⚡ Auto-assign${visibleOnly ? ` (${rows.length})` : ""}`}</button>
              <button className="oc-btn" onClick={() => setShowCoverage((s) => !s)}>{showCoverage ? "Hide areas" : "Driver areas"}</button>
              {proposal && <button className="oc-btn primary" onClick={commitAssign} disabled={assignBusy}>✓ Confirm assign</button>}
            </div>
            <div className="da-muted" style={{ marginTop: 4, paddingLeft: 4 }}>
              {visibleOnly
                ? `The ${rows.length} orders shown on the map above will be assigned — filter/review them first. Auto-assign proposes drivers by area, or bulk-assign the shown set below. Click any marker to assign it. Edit on the board, then Confirm.`
                : "The whole day's orders for the selected marketplace will be assigned."}
            </div>
            <div className="sa-controls" style={{ padding: "6px 4px 0", borderTop: "1px dashed #e2e8f0", marginTop: 8 }}>
              <label>Assign all {rows.length} shown to
                <select className="oc-input" value={bulkDriver} onChange={(e) => setBulkDriver(e.target.value)}>
                  <option value="">— pick driver —</option>
                  {driversRaw.map((d) => <option key={d.driver_id} value={d.driver_id}>{d.name}</option>)}
                </select>
              </label>
              <button className="oc-btn" disabled={!bulkDriver} onClick={() => assignShownTo(Number(bulkDriver))}>Assign shown →</button>
              <button className="oc-btn" onClick={() => assignShownTo(null)}>Unassign shown</button>
              <button className="oc-btn" onClick={() => buildFromRows(null)} title="Re-load the orders currently shown on the map into the board">↻ Reload shown</button>
            </div>
            {assignMsg && <div className={`da-msg ${assignMsg.type}`} style={{ marginTop: 8 }}>{assignMsg.text}</div>}
            {showCoverage && (
              <div style={{ marginTop: 10 }}>
                <AssignCoverage zones={zones} drivers={driversRaw} onChanged={loadDrivers} />
              </div>
            )}
          </div>
        )}

        {!gmReady && (
          <div style={{ height: 560, display: "grid", placeItems: "center", background: "#f5f5f5" }}>Loading map…</div>
        )}

        {assignOpen && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 8, padding: "8px 10px", background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 8 }}>
            <button
              type="button"
              className="oc-btn"
              onClick={() => { setMarqueeMode((v) => !v); setSelectedIds([]); }}
              style={marqueeMode ? { background: "#2563eb", color: "#fff" } : undefined}
            >
              {marqueeMode ? "✓ Selecting — drag a box on the map" : "▱ Select area (drag to pick orders)"}
            </button>
            {marqueeMode && (
              <>
                <span style={{ fontWeight: 700, color: selectedIds.length ? "#111827" : "#9ca3af" }}>
                  {selectedIds.length} selected
                </span>
                <select className="oc-input" value={marqueeDriver} onChange={(e) => setMarqueeDriver(e.target.value)} style={{ width: 200 }}>
                  <option value="">— choose driver —</option>
                  {driversRaw.map((d) => <option key={d.driver_id} value={d.driver_id}>{d.name}</option>)}
                </select>
                <button type="button" className="oc-btn" disabled={!selectedIds.length || !marqueeDriver} onClick={applyMarquee} style={{ background: "#16a34a", color: "#fff" }}>
                  Assign selected → driver
                </button>
                {selectedIds.length > 0 && (
                  <button type="button" className="oc-btn" onClick={() => setSelectedIds([])}>Clear</button>
                )}
                <span style={{ fontSize: 12, color: "#6b7280" }}>Tip: pick a driver, drag a box around the orders, then Assign.</span>
              </>
            )}
          </div>
        )}
        {gmReady && <OrdersMap rows={rows} zones={zones} assignment={assignOpen ? assignment : null}
          assignMode={assignOpen} drivers={proposal?.drivers || []} onReassign={reassign} height={560}
          marqueeMode={marqueeMode} onMarquee={setSelectedIds} selectedIds={selectedIds} />}
        {isMarketplaceAdmin && (
          <div style={{ marginTop: 8, textAlign: "right" }}>
            <Link to="/sales/map-zones" style={{ fontSize: 13, color: "#2563eb" }}>⚙ Edit map zones</Link>
          </div>
        )}
      </div>

      {/* Assignment board — appears under the map once auto-assigned. */}
      {assignOpen && proposal && (
        <div className="oc-card" style={{ marginTop: 16 }}>
          <div className="sa-summary">
            <b>{liveSummary.total}</b> orders · <b style={{ color: "#16a34a" }}>{liveSummary.assigned}</b> assigned · <b style={{ color: "#dc2626" }}>{liveSummary.unassigned}</b> unassigned
          </div>
          <div className="sa-board">
            {(proposal.drivers || []).map((d) => (
              <div className={"sa-col" + (selectedDriver === d.driver_id ? " sel" : "")} key={d.driver_id}
                   onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDropCard(e, d.driver_id)}
                   style={{ borderTop: `3px solid ${driverColors[d.driver_id]}` }}>
                <div className="sa-col-head" onClick={() => setSelectedDriver(selectedDriver === d.driver_id ? null : d.driver_id)}>
                  {d.name} <span className="sa-count">{(byDriver[d.driver_id] || []).length}</span>
                </div>
                <div className="sa-col-body">
                  {[...(byDriver[d.driver_id] || [])].sort((a, b) => (a.slot_label || "").localeCompare(b.slot_label || "") || (a.sort_order ?? 0) - (b.sort_order ?? 0)).map((o) => (
                    <div className="sa-card" key={o.order_id} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", String(o.order_id))}>
                      <span className="sa-dot" style={{ background: zColor(o.area) }} />
                      <div className="sa-card-main">
                        <div className="sa-card-top">#{o.order_id} <span className="da-muted">{o.name || ""}</span></div>
                        <div className="sa-card-sub">{o.area ? (zones[o.area]?.name_ar || o.area.replace(/_/g, " ")) : "no area"}{o.slot_label ? ` · ${o.slot_label}` : ""}</div>
                      </div>
                      <select className="sa-move" value={o.driver_id || ""} onChange={(e) => reassign(o.order_id, e.target.value ? Number(e.target.value) : null)}>
                        <option value="">— none —</option>
                        {(proposal.drivers || []).map((dd) => <option key={dd.driver_id} value={dd.driver_id}>{dd.name}</option>)}
                      </select>
                    </div>
                  ))}
                  {!(byDriver[d.driver_id] || []).length && <div className="da-muted sa-empty">Drop orders here</div>}
                </div>
              </div>
            ))}
            <div className="sa-col unassigned" onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDropCard(e, null)}>
              <div className="sa-col-head">Unassigned <span className="sa-count">{unassigned.length}</span></div>
              <div className="sa-col-body">
                {unassigned.map((o) => (
                  <div className="sa-card" key={o.order_id} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", String(o.order_id))} title={o.reason || ""}>
                    <span className="sa-dot" style={{ background: zColor(o.area) }} />
                    <div className="sa-card-main">
                      <div className="sa-card-top">#{o.order_id} <span className="da-muted">{o.name || ""}</span></div>
                      <div className="sa-card-sub">{o.reason || "unassigned"}</div>
                    </div>
                    <select className="sa-move" value="" onChange={(e) => reassign(o.order_id, e.target.value ? Number(e.target.value) : null)}>
                      <option value="">— none —</option>
                      {(proposal.drivers || []).map((dd) => <option key={dd.driver_id} value={dd.driver_id}>{dd.name}</option>)}
                    </select>
                  </div>
                ))}
                {!unassigned.length && <div className="da-muted sa-empty">— none —</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {failedRows.length > 0 && (
        <div className="oc-card" style={{ marginTop: 16 }}>
          <h3 style={{ color: "#e74c3c", marginTop: 0, fontSize: "16px" }}>
            Orders missing map coordinates ({failedRows.length})
          </h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {failedRows.map((r) => (
              <a
                key={r.order_id}
                href={`/orders/${r.order_id}`}
                target="_blank"
                rel="noreferrer"
                className="oc-btn ghost"
                style={{ padding: "6px 12px", fontSize: 13, height: "auto", minHeight: "auto", border: "1px solid #ddd" }}
              >
                Order #{r.order_id} ({r.customer || r.name || "-"})
              </a>
            ))}
          </div>
        </div>
      )}

      {!isLoading && rows.length === 0 && <div className="oc-card">No results</div>}

      <div className="oc-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
        <div>
          Showing page <strong>{meta.page}</strong> of <strong>{meta.pages}</strong> ({meta.total} total)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="oc-btn ghost" disabled={meta.page <= 1} onClick={() => setPage(1)}>
            « First
          </button>
          <button className="oc-btn ghost" disabled={meta.page <= 1} onClick={() => setPage(meta.page - 1)}>
            ‹ Prev
          </button>
          <button className="oc-btn ghost" disabled={meta.page >= meta.pages} onClick={() => setPage(meta.page + 1)}>
            Next ›
          </button>
          <button className="oc-btn ghost" disabled={meta.page >= meta.pages} onClick={() => setPage(meta.pages)}>
            Last »
          </button>
        </div>
      </div>
    </div>
  );
}

function OrdersMap({ rows, zones = {}, assignment = null, assignMode = false, drivers = [], onReassign = null, height = 520, marqueeMode = false, onMarquee = null, selectedIds = [] }) {
  const ref = React.useRef(null);
  const mapRef = React.useRef(null);
  const markersRef = React.useRef([]);
  const infoRef = React.useRef(null);
  const zonesRef = React.useRef([]);
  // Keep the latest assign props for marker-click handlers without rebuilding markers.
  const asgRef = React.useRef({});
  asgRef.current = { assignMode, drivers, onReassign, assignment };

  // Marquee (drag-to-select) support. The rectangle is drawn with direct DOM
  // writes (not React state) so dragging stays smooth — no re-render per mousemove.
  const overlayRef = React.useRef(null);   // OverlayView → gives us a pixel projection
  const pointsRef = React.useRef([]);      // [{ row, pos }] currently plotted
  const markersByIdRef = React.useRef({}); // order_id → { marker, baseIcon }
  const rectRef = React.useRef(null);      // the selection rectangle <div>
  const dragRef = React.useRef(null);      // { x1,y1,x2,y2 } while dragging

  // Enable/disable map panning so a drag selects instead of moving the map.
  React.useEffect(() => {
    const map = mapRef.current;
    if (map) map.setOptions({ draggable: !marqueeMode, gestureHandling: marqueeMode ? "none" : "auto" });
  }, [marqueeMode]);

  const paintRect = () => {
    const el = rectRef.current, d = dragRef.current, mapEl = ref.current;
    if (!el || !mapEl || !d) return;
    // The admin renders with html{zoom:.8}: mouse coords + getBoundingClientRect
    // are in VISUAL px, but a child's inline left/top are in LAYOUT px (which the
    // zoom then scales). Divide by the zoom factor (rendered ÷ layout width) so
    // the drawn box lines up exactly with the cursor.
    const box = mapEl.getBoundingClientRect();
    const sx = mapEl.offsetWidth ? box.width / mapEl.offsetWidth : 1;
    const sy = mapEl.offsetHeight ? box.height / mapEl.offsetHeight : 1;
    const x1 = Math.min(d.x1, d.x2), y1 = Math.min(d.y1, d.y2);
    el.style.left = (x1 / sx) + "px";
    el.style.top = (y1 / sy) + "px";
    el.style.width = (Math.abs(d.x2 - d.x1) / sx) + "px";
    el.style.height = (Math.abs(d.y2 - d.y1) / sy) + "px";
    el.style.display = "block";
  };
  const onMarqueeDown = (e) => {
    if (!marqueeMode) return;
    const box = ref.current.getBoundingClientRect();
    const x = e.clientX - box.left, y = e.clientY - box.top;
    dragRef.current = { x1: x, y1: y, x2: x, y2: y };
    paintRect();
  };
  const onMarqueeMove = (e) => {
    if (!marqueeMode || !dragRef.current) return;
    const box = ref.current.getBoundingClientRect();
    dragRef.current.x2 = e.clientX - box.left;
    dragRef.current.y2 = e.clientY - box.top;
    paintRect(); // direct DOM — smooth
  };
  const onMarqueeUp = () => {
    const d = dragRef.current;
    if (rectRef.current) rectRef.current.style.display = "none";
    dragRef.current = null;
    if (!marqueeMode || !d) return;
    const x1 = Math.min(d.x1, d.x2), y1 = Math.min(d.y1, d.y2);
    const x2 = Math.max(d.x1, d.x2), y2 = Math.max(d.y1, d.y2);
    const proj = overlayRef.current?.getProjection?.();
    if (!proj || (x2 - x1 < 3 && y2 - y1 < 3)) return;
    const ids = [];
    for (const { row, pos } of pointsRef.current) {
      const px = proj.fromLatLngToContainerPixel(new window.google.maps.LatLng(pos.lat, pos.lng));
      if (px && px.x >= x1 && px.x <= x2 && px.y >= y1 && px.y <= y2) ids.push(row.order_id);
    }
    onMarquee?.(ids);
  };

  React.useEffect(() => {
    const g = window.google;
    if (!g?.maps || !ref.current) return;

    if (!mapRef.current) {
      mapRef.current = new g.maps.Map(ref.current, {
        center: AMMAN,
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
      });
      infoRef.current = new g.maps.InfoWindow();
      // Invisible overlay just to expose a pixel projection for marquee selection.
      overlayRef.current = new g.maps.OverlayView();
      overlayRef.current.onAdd = function () {};
      overlayRef.current.draw = function () {};
      overlayRef.current.onRemove = function () {};
      overlayRef.current.setMap(mapRef.current);
    }

    const map = mapRef.current;
    const info = infoRef.current;

    for (const m of markersRef.current) m.setMap(null);
    markersRef.current = [];
    markersByIdRef.current = {};

    const points = rows
      .map((r) => ({ row: r, pos: getOrderCoords(r) }))
      .filter((x) => x.pos && Number.isFinite(x.pos.lat) && Number.isFinite(x.pos.lng));
    pointsRef.current = points; // for marquee selection

    if (!points.length) {
      map.setCenter(AMMAN);
      map.setZoom(12);
      return;
    }

    const jordanPoints = points.filter((p) => isInJordan(p.pos));

    for (const { row, pos } of points) {
      // In assign mode, colour the marker by its assigned driver (gray = unassigned).
      const asg = assignment ? assignment[row.order_id] : null;
      // Keep the teardrop PIN shape in assign mode too — just recolour it by the
      // assigned driver (gray = unassigned), instead of turning it into a dot.
      const icon = asg ? pinIcon(asg.color) : markerIconForOrder(row);
      const marker = new window.google.maps.Marker({
        position: pos,
        map,
        title: `Order ${row.order_id}`,
        icon,
      });
      markersByIdRef.current[row.order_id] = { marker, baseIcon: icon };

      marker.addListener("click", () => {
        const { assignMode: am, drivers: drvs, onReassign: reassignCb, assignment: asg } = asgRef.current;
        if (am && reassignCb) {
          // Assign / unassign this order right from the marker.
          const cur = asg ? asg[row.order_id] : null;
          const node = document.createElement("div");
          node.style.fontSize = "13px"; node.style.minWidth = "190px";
          node.innerHTML =
            `<b>#${row.order_id}</b> ${(row.customer || row.name || "")}<br>` +
            `<span style="color:#6b7280">Driver: ${cur?.driver_name || "— none —"}</span>`;
          const sel = document.createElement("select");
          sel.style.marginTop = "7px"; sel.style.width = "100%"; sel.style.padding = "3px";
          sel.innerHTML = `<option value="">— unassign —</option>` + (drvs || []).map((d) => `<option value="${d.driver_id}">${d.name}</option>`).join("");
          sel.value = cur?.driver_id ? String(cur.driver_id) : "";
          sel.addEventListener("change", () => { reassignCb(row.order_id, sel.value ? Number(sel.value) : null); infoRef.current.close(); });
          node.appendChild(sel);
          info.setContent(node);
        } else {
          info.setContent(renderInfoHtml(row));
        }
        info.open({ map, anchor: marker });
      });

      markersRef.current.push(marker);
    }

    if (!jordanPoints.length) {
      map.setCenter(AMMAN);
      map.setZoom(12);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    for (const { pos } of jordanPoints) bounds.extend(pos);

    map.fitBounds(bounds);

    const listener = window.google.maps.event.addListenerOnce(map, "idle", () => {
      const z = map.getZoom();
      if (typeof z === "number" && z > 16) map.setZoom(16);
      if (typeof z === "number" && z < 11) map.setZoom(12);
    });

    return () => {
      if (listener) window.google.maps.event.removeListener(listener);
    };
  }, [rows, assignment]);

  // Highlight the currently-selected markers (bigger blue ring) so it's obvious
  // which orders the marquee picked. Runs after markers are (re)built.
  React.useEffect(() => {
    if (!window.google?.maps) return;
    const sel = new Set((selectedIds || []).map(Number));
    Object.entries(markersByIdRef.current).forEach(([id, entry]) => {
      if (!entry?.marker) return;
      if (sel.has(Number(id))) {
        entry.marker.setIcon(pinIcon("#2563eb", 3.4)); // bigger blue pin = selected
        entry.marker.setZIndex(999);
      } else {
        entry.marker.setIcon(entry.baseIcon);
        entry.marker.setZIndex(null);
      }
    });
  }, [selectedIds, rows, assignment]);

  // Draw the (editable) Amman zone overlays under the order markers. Runs after
  // the map exists (map is created in the effect above) and redraws whenever the
  // zones change. clickable:false so markers/map clicks pass through and markers
  // always render on top.
  React.useEffect(() => {
    const g = window.google;
    const map = mapRef.current;
    if (!g?.maps || !map) return;

    for (const o of zonesRef.current) o.setMap(null);
    zonesRef.current = [];

    Object.entries(zones || {}).forEach(([name, zone]) => {
      const coords = (zone?.coordinates || [])
        .filter((c) => Number.isFinite(Number(c.lat)) && Number.isFinite(Number(c.lng)))
        .map((c) => ({ lat: Number(c.lat), lng: Number(c.lng) }));
      if (coords.length < 3) return;

      const polygon = new g.maps.Polygon({
        paths: coords,
        strokeColor: zone.color,
        strokeOpacity: 0.85,
        strokeWeight: 2,
        fillColor: zone.color,
        fillOpacity: 0.18,
        clickable: false,
        zIndex: 1,
        map,
      });
      const baseName = zone.name_ar || String(name).replace(/_/g, " ");
      const labelText =
        zone.zone_code !== null && zone.zone_code !== undefined && zone.zone_code !== ""
          ? `${baseName} (${zone.zone_code})`
          : baseName;
      const label = new g.maps.Marker({
        position: zoneCentroid(coords),
        map,
        clickable: false,
        zIndex: 1,
        icon: { path: g.maps.SymbolPath.CIRCLE, scale: 0 },
        label: { text: labelText, color: zone.color, fontSize: "12px", fontWeight: "700" },
      });
      zonesRef.current.push(polygon, label);
    });
  }, [zones]);

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <div style={{ width: "100%", height: "100%" }} ref={ref} />
      {marqueeMode && (
        <div
          onMouseDown={onMarqueeDown}
          onMouseMove={onMarqueeMove}
          onMouseUp={onMarqueeUp}
          onMouseLeave={onMarqueeUp}
          style={{ position: "absolute", inset: 0, cursor: "crosshair", zIndex: 5 }}
        >
          {/* Drawn via direct DOM writes in paintRect() — no React re-render. */}
          <div
            ref={rectRef}
            style={{
              position: "absolute", display: "none", left: 0, top: 0, width: 0, height: 0,
              border: "2px dashed #2563eb", background: "rgba(37,99,235,.15)", pointerEvents: "none",
            }}
          />
        </div>
      )}
    </div>
  );
}

function getOrderCoords(r) {
  const candidates = [
    { lat: r.pickup_lat, lng: r.pickup_lng },
    { lat: r.dropoff_lat, lng: r.dropoff_lng },
    { lat: r.lat, lng: r.lng },
    { lat: r.pickup_latitude, lng: r.pickup_longitude },
    { lat: r.dropoff_latitude, lng: r.dropoff_longitude },
  ];

  for (const c of candidates) {
    const lat = Number(c?.lat);
    const lng = Number(c?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) > 0.0001 && Math.abs(lng) > 0.0001) {
      return { lat, lng };
    }
  }

  // Try to decode MySQL WKB binary format
  const wkb = r?.order?.pickup_latlong || r?.order?.dropoff_latlong;
  const decoded = decodeMysqlPointWkb(wkb);
  if (decoded) return decoded;

  // Try to parse Plus Code from pickup_area or pickup_address
  const plusCode = r?.pickup_area || r?.pickup_address;
  if (plusCode && window.google?.maps?.LatLng) {
    try {
      // Use Google Maps Geocoder to resolve Plus Code
      const geocoder = new window.google.maps.Geocoder();
      let found = null;
      geocoder.geocode({ address: plusCode }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const loc = results[0].geometry.location;
          found = { lat: loc.lat(), lng: loc.lng() };
        }
      });
      if (found) return found;
    } catch (e) {}
  }

  // Fallback: show at Amman center with a warning (could be improved to show a special marker)
  return { ...AMMAN, _fallback: true };
}

function decodeMysqlPointWkb(value) {
  if (!value) return null;

  const bytes = new Uint8Array([...String(value)].map((ch) => ch.charCodeAt(0) & 0xff));
  const attempts = [0, 4];
  const results = [];

  for (const offset of attempts) {
    if (bytes.length < offset + 1 + 4 + 8 + 8) continue;

    const dv = new DataView(bytes.buffer, bytes.byteOffset + offset, bytes.byteLength - offset);
    const little = dv.getUint8(0) === 1;

    const type = dv.getUint32(1, little);
    if (type !== 1) continue;

    const x = dv.getFloat64(5, little);
    const y = dv.getFloat64(13, little);

    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

    const check = (a, b) => {
      if (isJordanLat(a) && isJordanLng(b)) return { lat: a, lng: b, jordan: true };
      if (isJordanLat(b) && isJordanLng(a)) return { lat: b, lng: a, jordan: true };

      const isLat = (v) => v >= -90 && v <= 90;
      const isLng = (v) => v >= -180 && v <= 180;

      if (isLat(a) && isLng(b)) return { lat: a, lng: b, jordan: false };
      if (isLat(b) && isLng(a)) return { lat: b, lng: a, jordan: false };

      return null;
    };

    const parsed = check(y, x) || check(x, y);
    if (parsed) results.push(parsed);
  }

  if (results.length === 0) return null;

  const jordan = results.find((r) => r.jordan);
  if (jordan) {
    delete jordan.jordan;
    return jordan;
  }

  const nonZero = results.find((r) => Math.abs(r.lat) > 0.0001 || Math.abs(r.lng) > 0.0001);
  if (nonZero) {
    delete nonZero.jordan;
    return nonZero;
  }

  delete results[0].jordan;
  return results[0];
}

function esc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function renderInfoHtml(r) {
  const id = esc(r.order_id);
  const ts = esc(r.next_ts || r.time_slot || r.timeslot || "-");
  const status = esc(r.status_text || r.status || "-");
  const customer = esc(r.customer || r.name || "-");
  const area = esc(r.area || r.payment_area || "-");
  const _payCode = r.payment || r.payment_method;
  const payment = esc(_payCode ? paymentInfo(_payCode).label : "-");
  const total = Number.isFinite(r.order_total) ? `${Number(r.order_total).toFixed(2)} JOD` : "-";
  const type = esc(labelForType(String(r.type || "-")));

  return `
    <div style="min-width:260px;max-width:340px">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:8px;padding-bottom:8px;border-bottom:1px solid #eee">
        <div><strong>DID: ${id}</strong></div>
        <div style="font-size:14px;font-weight:bold;color:#e74c3c;background:#fdf0ed;padding:2px 6px;border-radius:4px;">${ts}</div>
      </div>
      <div style="font-size:12px;line-height:1.45">
        <div><strong>Status:</strong> ${status}</div>
        <div><strong>Type:</strong> ${type}</div>
        <div><strong>Customer:</strong> ${customer}</div>
        <div><strong>Area:</strong> ${area}</div>
        <div><strong>Payment:</strong> ${payment}</div>
        <div><strong>Total:</strong> ${esc(total)}</div>
        <div style="margin-top:8px">
          <a href="/orders/${id}" target="_blank" rel="noreferrer" style="display:inline-block;padding:4px 8px;background:#3498db;color:#fff;text-decoration:none;border-radius:4px;">Open order</a>
        </div>
      </div>
    </div>
  `;
}

function labelForType(t) {
  if (t === "car_cleaning") return "Car Cleaning";
  if (t === "housekeeping") return "Housekeeping";
  if (t === "express") return "Express";
  if (t === "carpets") return "Carpets";
  if (t === "complementary") return "Complementary";
  if (t === "disinfection") return "Disinfection";
  if (t === "furniture") return "Furniture";
  if (t === "chemicals") return "Chemicals";
  if (!t) return "-";
  return t.charAt(0).toUpperCase() + t.slice(1);
}
